// react
import { createSlice, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store';

export interface ToastAction {
  label: string;
  href: string;
}

export interface ToastQueueItem {
  title: string;
  description?: string;
  variant?: 'success' | 'warning' | 'error' | 'info' | 'plain';
  action?: ToastAction;
  onAction?: () => void;
}

export interface ApiResponse {
  data: { message: string };
}

export interface UIState {
  toastQueue: ToastQueueItem[];
  dismissedBanners: Record<string, boolean>;
}

const toParagraph = (str: string): string => {
  const firstLetter = str?.[0].toUpperCase();
  const rest = str?.slice(1);
  return firstLetter + rest + '.';
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState: {
    toastQueue: [],
    dismissedBanners: {},
  } as UIState,
  reducers: {
    newToast: (state, action) => {
      state.toastQueue = [action.payload];
    },
    removeToast: (state) => {
      state.toastQueue = state.toastQueue.slice(1);
    },
    dismissBanner: (state, action: PayloadAction<string>) => {
      state.dismissedBanners[action.payload] = true;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(isRejected(), (state, action) => {
      const status = (action.payload as any)?.status;
      if (status === 202) return;
      if (status === 409) return;
      if (status === 410) return;
      if (status === 401) return;

      const description = (action.payload as ApiResponse)?.data?.message;
      if (!description) {
        return;
      }
      state.toastQueue = [
        {
          title: 'Error',
          description: toParagraph(description),
          variant: 'error',
        },
      ];
    });
  },
});

export const { newToast, removeToast, dismissBanner } = uiSlice.actions;

export const getToastQueue = (state: RootState) => state.ui.toastQueue;
export const getIsBannerDismissed = (state: RootState, bannerID: string) =>
  state.ui.dismissedBanners[bannerID] === true;

export default uiSlice.reducer;
